import React, { useEffect, useState } from 'react';
import { useLanguageContext } from "../context/languageContext";
import heroVideo from "../assets/hero-video.mp4";
import { ChevronDown, ArrowRight } from "lucide-react";

export default function Hero() {
  const { t } = useLanguageContext();
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    setLoaded(true);
  }, []);

  const scrollToMenu = () => {
    document.getElementById('menu')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    // min-h-[100svh] rather than h-screen: on phones the browser chrome makes
    // 100vh taller than the visible area, which pushed the CTA off screen.
    <section
      id="hero"
      className="relative min-h-[100svh] w-full overflow-hidden flex items-center justify-center bg-dark-chocolate"
    >
      {/* Video Background - Stable, no constant zoom */}
      <div className="absolute inset-0">
        <video
          className="w-full h-full object-cover opacity-60"
          autoPlay
          loop
          muted
          playsInline
        >
          <source src={heroVideo} type="video/mp4" />
        </video>
        {/* Professional Dark Overlay for Maximum Contrast */}
        <div className="absolute inset-0 bg-black/40" />
        <div className="absolute inset-0 bg-gradient-to-t from-dark-chocolate/90 via-transparent to-black/60" />
      </div>

      {/* Main Content - Clean & Centered */}
      <div className="relative z-10 container mx-auto px-6 flex flex-col items-center text-center">

        {/* Badge - Elegant & Minimal */}
        <div className={`mb-8 transition-opacity duration-1000 ${loaded ? 'opacity-100' : 'opacity-0'}`}>
          <span className="inline-block px-5 py-2 rounded-xl border border-gold/30 bg-black/50 backdrop-blur-md text-gold text-xs font-bold tracking-[0.3em] uppercase shadow-lg">
            {t("Est. 2006")}
          </span>
        </div>

        {/* Headline - Solid High Contrast, Ultra Professional */}
        <h1 className="font-serif text-5xl sm:text-7xl md:text-8xl lg:text-9xl font-bold leading-none tracking-tight mb-8 drop-shadow-2xl">
          <span className={`block text-cream transition-all duration-1000 delay-300 ${loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            {t("Ëmbëlsira")}
          </span>
          <span className={`block text-gold italic transition-all duration-1000 delay-500 ${loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            {t("Artizanale")}
          </span>
        </h1>

        {/* Subtitle - Refined Width & Leading */}
        <p className={`max-w-xl mx-auto text-lg sm:text-xl md:text-2xl text-gray-100 font-light leading-relaxed mb-12 tracking-wide transition-all duration-1000 delay-700 ${loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          {t("Where traditional Italian craftsmanship meets modern luxury. Experience the taste of pure passion.")}
        </p>

        {/* Single Strong CTA Button */}
        <div className={`transition-all duration-1000 delay-900 ${loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <button
            onClick={scrollToMenu}
            className="group relative px-10 py-5 bg-gold text-dark-chocolate font-bold text-sm tracking-[0.2em] uppercase overflow-hidden rounded-xl hover:text-white transition-colors duration-500 shadow-2xl"
          >
            <span className="relative z-10 flex items-center gap-3">
              {t("View Menu")}
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" />
            </span>
            <div className="absolute inset-0 bg-dark-chocolate transform scale-x-0 group-hover:scale-x-100 origin-left transition-transform duration-500 ease-out" />
          </button>
        </div>
      </div>

      {/* Scroll Indicator — a real button so it is keyboard reachable */}
      <button
        type="button"
        onClick={scrollToMenu}
        aria-label={t("View Menu")}
        className="absolute bottom-12 left-1/2 -translate-x-1/2 animate-bounce opacity-50 hover:opacity-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-gold rounded-full transition-opacity duration-300"
      >
        <ChevronDown className="w-10 h-10 text-white" />
      </button>
    </section>
  );
}